import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { EMPTY_FILTERS } from '@apps/web/features/coverage/model/coverage_types';
import {
    type CoverageSearchState,
    type CoverageSearchUpdate,
    useCoverageDashboard,
} from '@apps/web/features/coverage/model/use_coverage_dashboard';
import { isIsoDate } from '@apps/web/features/coverage/model/week_model';
import { CoveragePage } from '@apps/web/features/coverage/ui/coverage_page';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useMode } from '@apps/web/lib/mode_store';
import {
    useOrganization,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const dayPartSchema = z.enum(['morning', 'afternoon', 'evening', 'overnight']);

const coverageSearchSchema = z.object({
    view: z.enum(['grid', 'coverage', 'map']).optional().catch(undefined),
    week: z.string().refine(isIsoDate).optional().catch(undefined),
    offices: z.array(z.string()).optional().catch(undefined),
    cities: z.array(z.string()).optional().catch(undefined),
    zips: z.array(z.string()).optional().catch(undefined),
    days: z.array(z.number().int().min(0).max(6)).optional().catch(undefined),
    dayParts: z.array(dayPartSchema).optional().catch(undefined),
    tags: z.array(z.string()).optional().catch(undefined),
    employmentTypes: z.array(z.string()).optional().catch(undefined),
    availability: z.enum(['any', 'unknown_only']).optional().catch(undefined),
    match: z.enum(['every', 'any']).optional().catch(undefined),
    area: z.enum(['office', 'city', 'zip']).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    inactive: z.boolean().optional().catch(undefined),
    doNotContact: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/(main)/coverage')({
    component: CoverageRoute,
    validateSearch: coverageSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Coverage | Phoebe',
            },
        ],
    }),
});

export function CoverageRoute() {
    const organizationId = useOrganizationId();
    const mode = useMode();
    const {
        data: user,
        error: userError,
        mutate: retryUser,
    } = useUser({ reason: 'coverage-route-guard' });
    const {
        data: organization,
        error: organizationError,
        mutate: retryOrganization,
    } = useOrganization(organizationId, {
        reason: 'coverage-route-guard',
    });
    const navigate = useNavigate({ from: '/coverage' });
    const rawSearch = Route.useSearch();

    // The org feature flag is the only gate, so flagged organizations can
    // use the page directly. Without the flag the page redirects home.
    const canView = organization?.features?.coverage_dashboard_enabled === true;

    useEffect(() => {
        if (user && organization && !canView) {
            navigate({ to: '/', replace: true });
        }
    }, [user, organization, canView, navigate]);

    const clientsLayerEnabled = canView && mode === 'live';
    const search: CoverageSearchState = useMemo(
        () => ({
            ...EMPTY_FILTERS,
            view: rawSearch.view ?? 'grid',
            week: rawSearch.week,
            area: rawSearch.area ?? 'office',
            search: rawSearch.search ?? '',
            offices: rawSearch.offices ?? [],
            cities: rawSearch.cities ?? [],
            zips: rawSearch.zips ?? [],
            days: rawSearch.days ?? [],
            dayParts: rawSearch.dayParts ?? [],
            tags: rawSearch.tags ?? [],
            employmentTypes: rawSearch.employmentTypes ?? [],
            availability: rawSearch.availability ?? 'any',
            matchMode: rawSearch.match ?? 'every',
            includeInactive: rawSearch.inactive ?? false,
            includeDoNotContact: rawSearch.doNotContact ?? false,
        }),
        [rawSearch],
    );

    const onSearchChange = useCallback(
        (update: CoverageSearchUpdate) => {
            navigate({
                to: '/coverage',
                replace: true,
                search: (previous) => {
                    const next: z.infer<typeof coverageSearchSchema> = {
                        ...previous,
                    };
                    if (update.view !== undefined) {
                        next.view = update.view;
                    }
                    if ('week' in update) {
                        next.week = update.week;
                    }
                    if (update.area !== undefined) {
                        next.area = update.area;
                    }
                    if (update.search !== undefined) {
                        next.search = update.search;
                    }
                    if (update.offices !== undefined) {
                        next.offices = [...update.offices];
                    }
                    if (update.cities !== undefined) {
                        next.cities = [...update.cities];
                    }
                    if (update.zips !== undefined) {
                        next.zips = [...update.zips];
                    }
                    if (update.days !== undefined) {
                        next.days = [...update.days];
                    }
                    if (update.dayParts !== undefined) {
                        next.dayParts = [...update.dayParts];
                    }
                    if (update.tags !== undefined) {
                        next.tags = [...update.tags];
                    }
                    if (update.employmentTypes !== undefined) {
                        next.employmentTypes = [...update.employmentTypes];
                    }
                    if (update.availability !== undefined) {
                        next.availability = update.availability;
                    }
                    if (update.matchMode !== undefined) {
                        next.match =
                            update.matchMode === 'every'
                                ? undefined
                                : update.matchMode;
                    }
                    if (update.includeDoNotContact !== undefined) {
                        next.doNotContact =
                            update.includeDoNotContact || undefined;
                    }
                    if (update.includeInactive !== undefined) {
                        next.inactive = update.includeInactive || undefined;
                    }
                    // Drop empty/default values so shared URLs stay short.
                    for (const key of Object.keys(next) as Array<
                        keyof typeof next
                    >) {
                        const value = next[key];
                        if (
                            value === undefined ||
                            value === '' ||
                            (Array.isArray(value) && value.length === 0) ||
                            (key === 'view' && value === 'grid') ||
                            (key === 'area' && value === 'office') ||
                            (key === 'availability' && value === 'any') ||
                            (key === 'match' && value === 'every') ||
                            (key === 'inactive' && value === false) ||
                            (key === 'doNotContact' && value === false)
                        ) {
                            delete next[key];
                        }
                    }
                    return next;
                },
            });
        },
        [navigate],
    );

    // Gate fetching on the access check so unflagged sessions never fire
    // requests that would 403 while the redirect settles.
    const dashboard = useCoverageDashboard({
        organizationId: canView ? organizationId : null,
        search,
        onSearchChange,
    });

    const prerequisiteError = userError ?? organizationError;
    if (prerequisiteError) {
        return (
            <div className="space-y-3 p-6 text-center">
                <h1 className="text-xl font-semibold">Coverage</h1>
                <p className="text-muted-foreground text-sm">
                    We couldn’t verify your access to this organization.
                </p>
                <Button
                    variant="outline"
                    onClick={() => {
                        void Promise.all([retryUser(), retryOrganization()]);
                    }}
                >
                    Try again
                </Button>
            </div>
        );
    }

    if (!user || !organization) {
        return (
            <div
                className="space-y-3 p-4 lg:p-6"
                aria-label="Loading coverage access"
                aria-live="polite"
                role="status"
            >
                <span className="sr-only">Loading coverage access</span>
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-96 w-full rounded-xl" />
            </div>
        );
    }

    if (!canView) {
        return null;
    }

    return (
        <CoveragePage
            dashboard={dashboard}
            search={search}
            organizationId={organizationId}
            clientsLayerEnabled={clientsLayerEnabled}
            clientAccessKey={user?.id ?? ''}
        />
    );
}
